import { memo, useEffect, useId, useState } from "react";

import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  MenuArrowDownIcon,
  StarWithSparklesIcon,
  TrashIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { Snackbar } from "@js/components/snackbar";
import type { JobFormInterviewGradingCriteria } from "@js/types/jobs";

import { useGenerateGradingCriteriaDetailsMutation } from "../../api";
import { InterviewStyledTextField } from "../interview-styled-textfield";

type JobInterviewGradingCriteriaItemProps = {
  item: JobFormInterviewGradingCriteria;
  onChange: (
    id: number,
    newItem: Partial<JobFormInterviewGradingCriteria>,
  ) => void;
  onDelete: (id: number) => void;
  index: number;
  displayError?: boolean;
  jobId: number;
  error?: Record<string, string[]>;
  isDragging: boolean;
  getCurrentInterviewQuestions: () => string[];
};

const SCORE_FIELDS: Array<keyof JobFormInterviewGradingCriteria> = [
  "poor",
  "fair",
  "good",
  "very_good",
  "excellent",
];

export const JobInterviewGradingCriteriaItem = memo(
  ({
    item,
    index,
    onChange,
    onDelete,
    error,
    displayError,
    jobId,
    isDragging,
    getCurrentInterviewQuestions,
  }: JobInterviewGradingCriteriaItemProps) => {
    const id = useId();
    const [shouldShowGenerateButton, setShouldShowGenerateButton] = useState(
      !!item.isNew,
    );
    const [showDetails, setShowDetails] = useState(false);
    const [generateCriteria, { isLoading }] =
      useGenerateGradingCriteriaDetailsMutation();

    const definitionError = error?.definition?.[0];
    const nameError = error?.name?.[0];
    const isErrorInItem = !!error && !!Object.keys(error).length;

    useEffect(() => {
      if (!isErrorInItem) {
        return;
      }

      setShowDetails(true);
    }, [isErrorInItem]);

    const handleGenerateCriteriaClick = async () => {
      if (isLoading) {
        return;
      }

      try {
        const response = await generateCriteria({
          jobId,
          definition: item.definition,
          questions: getCurrentInterviewQuestions(),
        }).unwrap();

        setShouldShowGenerateButton(false);

        onChange(item.id, {
          ...response,
          definition: item.definition,
          isNew: false,
        });
      } catch (_error) {
        Snackbar.error("Something went wrong!");
      }
    };

    const showGenerateButton = shouldShowGenerateButton && item.definition;

    const defaultBorderColor = isDragging
      ? "var(--dark-violet)"
      : "var(--medium-violet)";
    const borderColor =
      displayError && isErrorInItem ? "var(--negative-2)" : defaultBorderColor;
    const backgroundColor = isDragging
      ? "var(--medium-violet)"
      : "var(--soft-violet)";
    return (
      <Box
        sx={{
          borderRadius: 2,
          backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
        className="transition"
        data-testid="interview-grading-criteria"
      >
        <Stack
          sx={{
            pr: 2,
            pl: 3,
            py: 3,
          }}
        >
          <Stack
            direction="row"
            sx={{ gap: 2, justifyContent: "space-between", mb: 2 }}
          >
            <Typography
              component="h3"
              variant="paragraph"
              size="medium"
              fontWeight={500}
            >
              Focus area {index + 1}
            </Typography>
            <Box sx={{ position: "relative" }}>
              <IconButton
                variant="transparent"
                onClick={() => onDelete(item.id)}
                size="x-small"
                aria-label="Remove focus area"
                sx={{
                  position: "absolute",
                  right: "-4px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <TrashIcon />
              </IconButton>
            </Box>
          </Stack>
          <InterviewStyledTextField
            id={`definition-${id}`}
            data-testid="grading-criteria-definition"
            value={item.definition}
            placeholder="Add a description for this evaluation criteria"
            onChange={(ev) => {
              setShouldShowGenerateButton(true);
              onChange(item.id, { definition: ev.target.value });
            }}
            sx={{ mb: 1.5 }}
            error={displayError && !!definitionError}
            helperText={definitionError}
          />
          <Typography
            component="p"
            variant="paragraph"
            color="grey-2"
            size="small"
            sx={{ mb: 1.5 }}
          >
            Describe the key skills or traits to assess. The more detailed you
            are, the better the AI can tailor the scorecard.
          </Typography>

          <Stack direction="row" sx={{ gap: 1 }}>
            <Button
              variant="transparent"
              sx={{ ml: -1, px: "8px !important" }}
              onClick={() => setShowDetails((prev) => !prev)}
              size="x-small"
            >
              View advanced scoring{" "}
              <Box sx={{ ml: 0.5, mb: "-6px" }}>
                <MenuArrowDownIcon
                  sx={{
                    fontSize: "1rem",
                    color: "inherit",
                    transform: showDetails ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Box>
            </Button>
            {showGenerateButton && (
              <Button
                variant="white-violet-dark-border"
                shape="squared"
                onClick={handleGenerateCriteriaClick}
                size="x-small"
                endIcon={isLoading ? <Loader size={20} /> : undefined}
                startIcon={<StarWithSparklesIcon />}
                sx={{
                  pointerEvents: isLoading ? "none" : undefined,
                }}
              >
                {isLoading && "Generating..."}
                {!isLoading && (
                  <>{item.isNew ? "Generate" : "Regenerate"} advanced scoring</>
                )}
              </Button>
            )}
          </Stack>
          <Collapse in={showDetails} mountOnEnter unmountOnExit>
            <Stack sx={{ mt: 3 }}>
              <InterviewStyledTextField
                id={`title-${id}`}
                label="Title (shown on scorecard)"
                value={item.name}
                placeholder="Add a title for this evaluation criteria"
                onChange={(ev) => {
                  onChange(item.id, { name: ev.target.value });
                }}
                sx={{
                  mb: 2,
                  label: {
                    mb: "4px !important",
                    span: { fontSize: "14px !important" },
                  },
                }}
                slotProps={{
                  input: {
                    notched: false,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          component="span"
                          variant="paragraph"
                          size="small"
                          color="grey-3"
                        >
                          {item.name.length}/100
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                }}
                error={displayError && !!nameError}
                helperText={nameError}
              />
              <Typography
                component="p"
                variant="paragraph"
                sx={{ fontSize: "14px !important", mb: 3 }}
              >
                <Typography
                  component="span"
                  variant="paragraph"
                  fontWeight={500}
                  sx={{ fontSize: "inherit !important" }}
                >
                  Scoring scale{" "}
                </Typography>
                <Typography
                  component="span"
                  variant="paragraph"
                  color="grey-2"
                  sx={{
                    ml: 1,
                    display: "inline-block",
                    fontSize: "inherit !important",
                  }}
                >
                  Describe what each scale from 1 (least proficient) to 5 (most
                  proficient) means.
                </Typography>
              </Typography>

              <Stack sx={{ gap: 2 }}>
                {SCORE_FIELDS.map((field, scoreIndex) => {
                  const fieldError = error?.[field]?.[0];
                  return (
                    <InterviewStyledTextField
                      key={field}
                      multiline
                      maxRows={4}
                      id={`field-${id}-${field}`}
                      label={`Score ${scoreIndex + 1}`}
                      value={item[field]}
                      placeholder="Add a definition for this score"
                      onChange={(ev) => {
                        onChange(item.id, { [field]: ev.target.value });
                      }}
                      sx={{
                        label: {
                          mb: "4px !important",
                          span: { fontSize: "14px !important" },
                        },
                      }}
                      error={displayError && !!fieldError}
                      helperText={fieldError}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      </Box>
    );
  },
);
